import { useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { GetIFRSById } from "../../../../api/Quiz/GetIFRSById";
import { GetIFRSAttemptAnswers } from "../../../../api/Quiz/GetIFRSAttemptAnswers";
import Spinner from "../../../../components/Shared/Spinner";
import SpreadsheetPreview from "../../../../components/common/SpreadsheetPreview";

const ResultsPage = () => {
  const { exam_id } = useParams<{ exam_id: string }>();
  const navigate = useNavigate();

  const studentExamId = useMemo(() => {
    if (!exam_id) return "";
    return localStorage.getItem(`ifrs-student-exam-id-${exam_id}`) || "";
  }, [exam_id]);

  const location = useLocation();
  const backUrl = useMemo(() => {
    if (!exam_id) return "/";
    return (
      (location.state as { from?: string })?.from ||
      sessionStorage.getItem(`ifrs-back-url-${exam_id}`) ||
      "/"
    );
  }, [exam_id, location.state]);

  const { data: examResponse, isLoading: isExamLoading } = useQuery({
    queryKey: ["ifrs-exam", exam_id],
    queryFn: () => GetIFRSById(exam_id!),
    enabled: !!exam_id,
    refetchOnWindowFocus: false,
  });

  const { data: answersResponse, isLoading: isAnswersLoading } = useQuery({
    queryKey: ["ifrs-attempt-answers", exam_id, studentExamId],
    queryFn: () => GetIFRSAttemptAnswers(exam_id!, studentExamId),
    enabled: !!exam_id && !!studentExamId,
    refetchOnWindowFocus: false,
  });

  const examData = examResponse?.data;
  const answers = answersResponse?.data?.data || [];

  if (isExamLoading || isAnswersLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
        <Spinner />
      </div>
    );
  }

  const hasModelAnswer = !!examData?.modelAnswerUrl;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#f1f5f9", overflow: "hidden" }}>
      {/* Header */}
      <div style={{
        padding: "1rem 1.5rem",
        background: "#1e293b",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <div>
          <p style={{ fontSize: "0.75rem", color: "#94a3b8", marginBottom: "2px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Exam Results</p>
          <h1 style={{ fontSize: "1.1rem", fontWeight: 700, margin: 0 }}>{examData?.title}</h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.85rem", color: "#94a3b8" }}>
          <span>{answers.length} answer{answers.length !== 1 ? "s" : ""} submitted</span>
        </div>
      </div>

      {/* Content */}
      <div style={{
        flex: 1,
        display: "grid",
        gridTemplateColumns: hasModelAnswer ? "1fr 1fr" : "1fr",
        gap: "1px",
        overflow: "hidden",
        background: "#cbd5e1",
      }}>
        {/* Model Answer Panel */}
        {hasModelAnswer && (
          <div style={{ display: "flex", flexDirection: "column", background: "#fff", overflow: "hidden" }}>
            <div style={{
              padding: "0.75rem 1.25rem",
              background: "#0f172a",
              color: "#e2e8f0",
              fontWeight: 600,
              fontSize: "0.875rem",
              letterSpacing: "0.03em",
              flexShrink: 0,
            }}>
              Model Answer
            </div>
            <iframe
              src={examData!.modelAnswerUrl!}
              title="Model Answer"
              style={{ flex: 1, width: "100%", border: "none" }}
            />
          </div>
        )}

        {/* Student Answers Panel */}
        <div style={{ display: "flex", flexDirection: "column", background: "#fff", overflow: "hidden" }}>
          <div style={{
            padding: "0.75rem 1.25rem",
            background: "#0f172a",
            color: "#e2e8f0",
            fontWeight: 600,
            fontSize: "0.875rem",
            letterSpacing: "0.03em",
            flexShrink: 0,
          }}>
            Your Answers
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "1.25rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            {answers.length === 0 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "200px", color: "#94a3b8", fontSize: "0.95rem" }}>
                No answers submitted.
              </div>
            ) : (
              answers.map((answer, index) => (
                <div
                  key={`${answer.questionId}-${answer.responseOptionId}-${index}`}
                  style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
                >
                  <div style={{
                    padding: "0.6rem 1rem",
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color: "#475569",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}>
                    <span style={{
                      background: "#0f172a",
                      color: "#fff",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      flexShrink: 0,
                    }}>{index + 1}</span>
                    Answer #{index + 1}
                  </div>
                  <div style={{ padding: "1rem", minHeight: "80px" }}>
                    {answer.answerFileUrl || answer.answerFilePath ? (
                      <a
                        href={(answer.answerFileUrl || answer.answerFilePath)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        style={{ color: "#3b82f6", textDecoration: "underline", fontSize: "0.9rem" }}
                      >
                        Download your uploaded answer ↓
                      </a>
                    ) : answer.answerData?.type === "wordProcessor" ? (
                      <div
                        className="default-text-previewer"
                        dangerouslySetInnerHTML={{ __html: answer.answerData.data || "" }}
                        style={{ fontSize: "0.9rem", lineHeight: 1.7, color: "#1e293b" }}
                      />
                    ) : answer.answerData?.type === "spreadsheet" ? (
                      <div style={{ overflowX: "auto" }}>
                        <SpreadsheetPreview cells={answer.answerData.data} />
                      </div>
                    ) : (
                      <p style={{ color: "#94a3b8", fontSize: "0.875rem", fontStyle: "italic" }}>No answer data</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding: "0.875rem 1.5rem",
        background: "#fff",
        borderTop: "1px solid #e2e8f0",
        display: "flex",
        justifyContent: "flex-end",
        flexShrink: 0,
      }}>
        <button
          onClick={() => navigate(backUrl)}
          style={{
            padding: "0.6rem 1.4rem",
            background: "#1e293b",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            fontSize: "0.875rem",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
          }}
        >
          Finish
        </button>
      </div>
    </div>
  );
};

export default ResultsPage;
